import React, { useCallback } from 'react'

import { Box, Skeleton, Typography } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import { ActionButton, SkillBiteExpertChip } from '../../../../components'
import { AddToPlaylistButton } from '../../../../components/action-buttons'
import { useTranslation } from '../../../../hooks'
import SkillBiteCardContainer from './skill-bite-card-container'
import { SkillBiteInfoContainer } from './skill-bite-card-info-container'
import { DynamicFeedTwoTone, EmojiEventsTwoTone, NotificationsTwoTone } from '@mui/icons-material'

export const useStyles = makeStyles(
  ({ palette, typography, spacing, breakpoints, props }) => ({
    cardContent: {
      display: 'flex',
      flexDirection: props => props.variant !== 'generic' ? 'row' : 'column-reverse',
      gridGap: 20
    },
    skillBiteImage: {
      borderRadius: '8px',
      width: props => props.variant === 'main' ? '250px' : '100%',
      height: props => props.variant === 'main' ? '140px' : '100%',
      aspectRatio: 16 / 9,
      objectFit: 'cover'
    }
  })
)

const SkillBiteCard = ({
  skillBite,
  isLoading,
  onSubscribe = (skillBite) => { },
  hideSubscribeButton = false,
  onClick = (skillBite) => { },
  shouldDisableSubscribeButton = false,
  onAddToPlaylist,
  addToPlaylistButtonSize = 'xs',
  ...props
}) => {
  const classes = useStyles(props)
  const { t } = useTranslation('skill_bite')
  const hasSubscription = !!skillBite?.subscription
  const isSubscriptionPaused = hasSubscription && skillBite?.subscription?.pausedAt

  function handleNavigate () {
    onClick(skillBite)
  }

  function handleSubscribe (evt) {
    evt.stopPropagation()
    onSubscribe(skillBite)
  }

  const renderInfo = useCallback(() => {
    if (isLoading) return <Skeleton width='100px' height='40px' />

    const { expert } = skillBite || {}

    if (!hasSubscription) {
      return (
        <Box width='100%' display='flex' flexDirection='row' justifyContent='space-between'>
          <Box display='flex' flexDirection='row' alignItems='center' gap={1}>
            {/* <LayersTwoTone fontSize='small' /> */}
            <Typography variant='body1'> {t('session_count', { sessionCount: skillBite?.sessionCount })}</Typography>
          </Box>
          {expert && <SkillBiteExpertChip />}
        </Box>
      )
    }

    const { sessionCount, completedSessionCount, isComplete } = skillBite || {}

    if (isSubscriptionPaused) {
      return (
        <Box display='flex' flexDirection='row' alignItems='center' gap={1} sx={{ color: '#FFA500' }}>
          <NotificationsTwoTone fontSize='small' color='inherit' />
          <Typography variant='body1'>{t('paused')}</Typography>
        </Box>
      )
    }

    if (isComplete) {
      return (
        <Box display='flex' flexDirection='row' alignItems='center' gap={1} sx={{ color: '#0178E6' }}>
          <EmojiEventsTwoTone fontSize='small' color='inherit' />
          <Typography variant='body1' sx={{ fontWeight: 600 }}>{t('completed')}</Typography>
        </Box>
      )
    }

    return (
      <Box display='flex' flexDirection='row' alignItems='center' gap={1} sx={{ color: '#007D0C' }}>
        <NotificationsTwoTone fontSize='small' color='inherit' />
        <Typography variant='body1' sx={{ fontSize: 14 }}>{t('subscribed')}: <span style={{ fontWeight: 600 }}> {t('session_progress', { completedSessionCount, sessionCount })}</span></Typography>
      </Box>
    )
  }, [isLoading, skillBite, hasSubscription, isSubscriptionPaused, t])

  const renderCardImage = useCallback(() => {
    if (isLoading) {
      return (
        <Skeleton className={classes.skillBiteImage} />
      )
    } else {
      return (
        <Box>
          <img
            width={props.variant === 'main' ? 250 : '100%'}
            height={props.variant === 'main' ? 140 : '100%'}
            className={classes.skillBiteImage}
            src={skillBite.image.imgUrl}
            alt={skillBite?.name || 'Skill bite thumbnail'}
            data-test={`skill-bite-card-image-${skillBite?.name}`}
          />
          {props.variant === 'generic' && (
            <Box mt={1}>
              {renderInfo()}
            </Box>
          )}
        </Box>
      )
    }
  }, [isLoading, classes, props.variant, skillBite, renderInfo])

  function handleAddToPlaylistClick (evt) {
    evt.stopPropagation()
    if (onAddToPlaylist) onAddToPlaylist(skillBite)
  }

  function renderCardActions () {
    if (isLoading) {
      return (
        <Skeleton height={40} width={80} />
      )
    }

    if (hasSubscription) {
      return (
        <Box display='flex' flexDirection='row' gap='16px' alignItems='center' flexWrap='wrap'>
          <ActionButton
            variant='neutral'
            label={t('see_details')}
            data-test={`skill-bite-card-see-detail-button-${skillBite?.name}`}
          />
          {onAddToPlaylist && (
            <AddToPlaylistButton
              size={addToPlaylistButtonSize}
              variant='secondary-noselect'
              onClick={handleAddToPlaylistClick}
              aria-label='Add this skill bite to a playlist'
            />
          )}
        </Box>
      )
    }

    return (
      <Box display='flex' flexDirection='row' gap='16px' alignItems='center' flexWrap='wrap'>
        {!hideSubscribeButton && (
          <ActionButton
            disabled={shouldDisableSubscribeButton}
            label={t('subscribe')}
            variant='neutral'
            startIcon={<DynamicFeedTwoTone />}
            onClick={handleSubscribe}
            data-test={`skill-bite-card-subscribe-button-${skillBite?.name}`}
          />
        )}
        <ActionButton
          variant='neutral'
          label={t('see_details')}
          data-test={`skill-bite-card-see-detail-button-${skillBite?.name}`}
        />
        {onAddToPlaylist && (
          <AddToPlaylistButton
            size={addToPlaylistButtonSize}
            variant='secondary-noselect'
            onClick={handleAddToPlaylistClick}
            aria-label='Add this skill bite to a playlist'
          />
        )}
      </Box>

    )
  }

  return (
    <SkillBiteCardContainer onClick={handleNavigate} variant={props.variant}>
      {props.variant !== 'generic' && (
        <Box mt={1}>
          {renderInfo()}
        </Box>
      )}
      <Box className={classes.cardContent}>
        <SkillBiteInfoContainer isLoading={isLoading} name={skillBite?.name} variant={props.variant} imageUrl={skillBite?.image?.imgUrl} description={skillBite?.shortDescription} />
        <Box>
          {props.variant !== 'secondary' && renderCardImage()}
        </Box>
      </Box>
      <Box mt={1}>
        {renderCardActions()}
      </Box>
    </SkillBiteCardContainer>
  )
}

export default SkillBiteCard
